import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import { useMediaQuery } from '@givebutter/react-util';
import {
    DismissPromptDialog,
    FloatingChecklistDismissButton,
} from 'components/required-checklist/FloatingChecklistDismissButton';
import { posthogEvents } from 'constants/posthog-events';
import { useAccountLocalStorage } from 'contexts/useAccountLocalStorage';
import { usePosthog } from 'customHooks/usePosthog';
import { getGenericPathFromPath } from 'helpers/formatting';
import ButterDish from 'images/required-checklist/mrbutter-checklist-floating-butter-dish.svg?react';
import ButterSignKeepGoing from 'images/required-checklist/mrbutter-checklist-floating-sign-keepgoing.svg?react';
import ButterSign from 'images/required-checklist/mrbutter-checklist-floating-sign.svg?react';
import MrButterIconJumping from 'images/required-checklist/mrbutter-checklist-jumping.svg?react';
import MrButtonLeftArmKeepGoing from 'images/required-checklist/mrbutter-checklist-leftarm-keepgoing.svg?react';
import MrButtonLeftArm from 'images/required-checklist/mrbutter-checklist-leftarm.svg?react';
import MrButtonRightArmKeepGoing from 'images/required-checklist/mrbutter-checklist-rightarm-keepgoing.svg?react';
import MrButtonRightArm from 'images/required-checklist/mrbutter-checklist-rightarm.svg?react';
import MrButterIcon from 'images/required-checklist/mrbutter-checklist.svg?react';
import { twMerge } from 'tailwind-merge';

const ChecklistMrButter = ({
    keepGoing,
    faceLeft,
}: {
    keepGoing?: boolean;
    faceLeft?: boolean;
}) => {
    const RightArm = keepGoing ? MrButtonRightArmKeepGoing : MrButtonRightArm;
    const LeftArm = keepGoing ? MrButtonLeftArmKeepGoing : MrButtonLeftArm;
    return (
        <div className="size-[90px] shrink-0" aria-hidden>
            <div className="relative flex h-[68px] items-end">
                {faceLeft ? (
                    <RightArm className="animate-pivotShake absolute -left-1 -top-4 origin-bottom-right" />
                ) : (
                    <LeftArm className="animate-pivotShake absolute -right-1 -top-3 origin-bottom-left" />
                )}
                <MrButterIcon
                    className={twMerge('absolute h-14', faceLeft && 'right-0 -scale-x-100')}
                />
            </div>
        </div>
    );
};

const FloatingChecklistMrButter = ({
    jumping,
    keepGoing,
}: {
    jumping?: boolean;
    keepGoing?: boolean;
}) => {
    const armClass = twMerge(
        'absolute -left-1 -right-1 -top-4 origin-bottom-right',
        jumping ? 'rotate-12' : 'rotate-0',
    );
    return (
        <div className="size-[90px] shrink-0" aria-hidden>
            <div
                className={twMerge(
                    'relative flex h-[68px] items-end transition-[margin-top] duration-300 ease-in',
                    jumping ? '-mt-8' : 'mt-0',
                )}
            >
                {keepGoing ? (
                    <MrButtonRightArmKeepGoing className={armClass} />
                ) : (
                    <MrButtonRightArm className={armClass} />
                )}
                {jumping ? (
                    <MrButterIconJumping className="absolute right-1 h-14" />
                ) : (
                    <MrButterIcon className="absolute right-0 h-14 -scale-x-100" />
                )}
            </div>
        </div>
    );
};

const numClicksForPrompt = 3;

interface ChecklistButterStickProps extends Omit<ButtonProps, 'variant' | 'children'> {
    keepGoing?: boolean;
}
const ChecklistButterStick = ({ keepGoing, className, ...rest }: ChecklistButterStickProps) => {
    const { pushEvent } = usePosthog();
    const [dismissedOnThisPage, setDismissedOnThisPage] = useState(false);
    const [dismissedForThisSession, setDismissedForThisSession] = useState(false);
    const [answeredPopup, setAnsweredPopup] = useState(false);
    const [numTimesClicked, setNumTimesClicked] = useState(0);
    const { permanentlyDismissFloatingChecklist, setPermanentlyDismissFloatingChecklist } =
        useAccountLocalStorage(
            ({ permanentlyDismissFloatingChecklist, setPermanentlyDismissFloatingChecklist }) => ({
                permanentlyDismissFloatingChecklist,
                setPermanentlyDismissFloatingChecklist,
            }),
        );
    const location = useLocation();
    const isMobile = useMediaQuery(`(max-width: 640px)`);

    useEffect(() => {
        setDismissedOnThisPage(false);
    }, [location.pathname]);

    const logLongtermDismiss = useCallback(
        (type: 'session' | 'permanent') => {
            const pagePath = getGenericPathFromPath(location.pathname);
            pushEvent(posthogEvents.FLOATING_CHECKLIST_LONGTERM_DIALOG_DISMISSED, {
                type,
                pagePath,
            });
        },
        [pushEvent, location.pathname],
    );

    const logDismissClick = useCallback(() => {
        const pagePath = getGenericPathFromPath(location.pathname);
        pushEvent(posthogEvents.FLOATING_CHECKLIST_DISMISSED, {
            numTimesClicked: numTimesClicked + 1,
            pagePath,
        });
    }, [pushEvent, location.pathname, numTimesClicked]);

    const showPrompt = useMemo(
        () =>
            ((isMobile && numTimesClicked >= 1) ||
                (!isMobile && numTimesClicked === numClicksForPrompt)) &&
            !answeredPopup,
        [numTimesClicked, answeredPopup, isMobile],
    );

    if (
        dismissedForThisSession ||
        permanentlyDismissFloatingChecklist ||
        (dismissedOnThisPage && !showPrompt)
    ) {
        return null;
    }

    const signClass =
        'animate-pivotShake absolute -top-3 right-7 w-12 origin-bottom sm:-top-6 sm:right-11 sm:w-20';
    return (
        <div className="group mb-0 transition-all hover:mb-3">
            <Button
                aria-label="Open Onboarding Checklist"
                variant="none"
                className={twMerge('relative', className)}
                {...rest}
            >
                <ButterDish className="w-24 sm:w-40" />
                {keepGoing ? (
                    <ButterSignKeepGoing className={signClass} />
                ) : (
                    <ButterSign className={signClass} />
                )}
            </Button>

            <FloatingChecklistDismissButton
                onClick={() => {
                    setDismissedOnThisPage(true);
                    setNumTimesClicked(prev => prev + 1);
                    logDismissClick();
                }}
                className="group-hover:opacity-100"
            />

            <DismissPromptDialog
                open={showPrompt}
                onOpenChange={open => {
                    if (!open) {
                        setAnsweredPopup(true);
                    }
                }}
            >
                <div className="flex flex-col gap-2 sm:flex-row">
                    <Button
                        variant="outline"
                        color="neutral"
                        className="w-full sm:w-1/2"
                        onClick={() => {
                            setPermanentlyDismissFloatingChecklist(true);
                            logLongtermDismiss('permanent');
                        }}
                    >
                        Hide forever
                    </Button>
                    <Button
                        variant="solid"
                        className="w-full sm:w-1/2"
                        onClick={() => {
                            setDismissedForThisSession(true);
                            logLongtermDismiss('session');
                        }}
                    >
                        Hide for this session
                    </Button>
                </div>
            </DismissPromptDialog>
        </div>
    );
};

export { ChecklistMrButter, FloatingChecklistMrButter, ChecklistButterStick };
